import React from "react";
import {
  Box,
  Grid,
  Text,
  Input,
  FormLabel,
  FormControl,
  Flex,
  Button,
} from "@chakra-ui/react";
import { InfoIcon } from '@chakra-ui/icons'
import { v4 as uuidv4 } from 'uuid';
import { FaPlus, FaUpload, FaBrain, FaFileImport, FaFileUpload } from "react-icons/fa";
import { ModalBox } from "../../components/ModalBox";
import { FormInput } from "../../components/FormInput";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { useCreateNewAIDynoWithTemplate } from "./useCreateNewAIDynoWithTemplate"
import { ResourcesPage } from "../ResourcesPage";
import { OptionCard } from "../../components/OptionCard";
import { FormSelect } from "../../components/FormSelect";
import { ADA, AVA, BRANDON, OLLIE } from "../../constants";

const typeMap = {
  "STATIC": "Use this for straightforward explanations, introductions, or learning objectives where no interaction is needed.",
  "QUICKCHECK": "Best used right after a key concept to instantly test understanding with a short question and a “Check” button.",
  "SELFCHECK": "Use this to encourage learners to reflect on the concept by answering a prompt — feedback is given immediately without a button. Ideal for mid-topic pauses.",
  "DEEPDIVE": "Present a multiple-choice question with a Quick Check, then offer an optional explanation toggle. Use this when you want learners to not only pick an answer but also understand why. Ideal for deeper concept reinforcement or common misconceptions.",
}

export const CreateNewAIDynoWithTemplatePage = ({ selectedDynoType }) => {
  const {
    isOpen,
    onOpen,
    onCancel,
    createDynoUsingPrompt,
    loading,
    createDyno,
    selectMedia,
    mediaUrl,
    isPromptOpen,
    onPromptOpen,
    isBrowseOpen,
    onBrowseOpen,
    isCreateFromPDFOpen,
    onCreateFromPDFOpen,
    isCreateFromPPTOpen,
    onCreateFromPPTOpen,
    isEmbedFileOpen,
    onEmbedFileOpen,
    setNewFile,
    newFile,
    uploadAndCreateEddoc,
    isUploading,
    extractText,
    setUserMessage,
    userMessage,
    embedFileinDyno,
    uploadProgress,
    previewVoice,
    generateVideoDyno,
    generatePptDyno,
    audioRef,
    publisher,
    isTokenExhaustOpen,
    onTokenExhaustOpen,
    onTokenExhaustClose,
    buyCredits,
    isTokenExhaustUpgradeToProOpen,
    onTokenExhaustUpgradeToProOpen,
    onTokenExhaustUpgradeToProClose,
    upgradeToPro,
    isUpgradeToProOpen,
    onUpgradeToProOpen,
    onUpgradeToProClose,
    contentType,
    setContentType
  } = useCreateNewAIDynoWithTemplate({ selectedDynoType });

  // Dynamic descriptions for non-video creation tiles
  const isReading = selectedDynoType.name === "Reading";
  const descBlank = isReading
    ? "Start with a blank page to craft your lesson: write content, add images, and organize sections for clear, focused reading."
    : "Start fresh with a blank template and build your assessment or practice set with our intuitive editor.";
  const descUploads = isReading
    ? "Use existing files (PDFs, images, audio) to build a reading lesson. Extract key text, embed media, and structure it for learners."
    : "Upload existing worksheets or question banks and convert them into interactive practice materials.";
  const descImportNew = isReading
    ? "Upload a new file and turn it into a structured reading with embedded media and supporting resources."
    : "Upload a new file to automatically convert it into interactive practice content.";
  const descAI = isReading
    ? "Let AI draft a reading from your topic. Refine the content, add examples, and enrich with media."
    : "Let AI generate practice questions and prompts from your topic or learning objective.";

  return (
    <>
      {loading && <LoadingOverlay />}
      <Box background="white" padding="30px" boxShadow="lg" borderRadius="20px">
        <Text>2. How would you like to create it?</Text>
        <Grid templateColumns={{ base: "1fr", md: "repeat(1, 1fr)", lg: "repeat(3, 1fr)" }} gap={6} mt={3} alignContent="stretch">
          {/* Create a Blank Doc Card */}
          {
            selectedDynoType.name === "Video" ?
              <>
                <OptionCard 
                  icon={FaUpload} 
                  title="Create Presentations from PDFs" 
                  description="Effortlessly transform your PDFs into well-structured presentations with auto-generated slides, speaker notes, and seamless EdDoc integration. Generated PPTs are stored under Resources and can be directly used to create engaging videos." 
                  onClick={!publisher.isPro ? onUpgradeToProOpen :  publisher.creditsAvailable + publisher.extraCreditsAvailable > 0 ? onCreateFromPDFOpen : onTokenExhaustOpen} 
                  isPro={publisher.isPro} 
                  isVideo={true} 
                  isAI={true}
                />
                <OptionCard 
                  icon={FaUpload} 
                  jobKey="convert-video" 
                  title="Create Videos from PPT" 
                  description="Select your PowerPoint presentations from Resources and convert them into engaging videos. The narration is automatically generated from the presentation's speaker notes, seamlessly integrated into EdDoc for interactive learning experiences." 
                  onClick={!publisher.isPro ? onUpgradeToProOpen :  publisher.creditsAvailable + publisher.extraCreditsAvailable > 0 ? onCreateFromPPTOpen : onTokenExhaustOpen} 
                  isPro={publisher.isPro} 
                  isVideo={true} 
                  isAI={true}
                />
                <OptionCard 
                  icon={FaFileImport} 
                  title="Embed File into EdDoc" 
                  description="Import and embed external files directly into your EdDoc to create comprehensive learning materials." 
                  onClick={!publisher.isPro ? onUpgradeToProOpen :  publisher.creditsAvailable + publisher.extraCreditsAvailable > 0 ? onEmbedFileOpen : onTokenExhaustOpen} 
                  isPro={publisher.isPro} 
                  isVideo={true} 
                />
              </>
              :
              <>
                <OptionCard 
                  icon={FaPlus} 
                  title="Create from Blank" 
                  description={descBlank} 
                  onClick={createDyno} 
                />
                <OptionCard 
                  icon={FaUpload} 
                  title="Create from Uploads" 
                  description={descUploads} 
                  onClick={publisher.creditsAvailable + publisher.extraCreditsAvailable > 0 ? onOpen : publisher.isPro ? onTokenExhaustOpen : onTokenExhaustUpgradeToProOpen} 
                  isAI={true}
                />
                <OptionCard 
                  icon={FaFileUpload} 
                  title="Import New File" 
                  description={descImportNew} 
                  onClick={publisher.creditsAvailable + publisher.extraCreditsAvailable > 0 ? onBrowseOpen : publisher.isPro ? onTokenExhaustOpen : onTokenExhaustUpgradeToProOpen} 
                  isAI={true}
                />
                <OptionCard 
                  icon={FaBrain} 
                  title="Create from LLM" 
                  description={descAI} 
                  onClick={publisher.creditsAvailable + publisher.extraCreditsAvailable > 0 ? onPromptOpen : publisher.isPro ? onTokenExhaustOpen : onTokenExhaustUpgradeToProOpen} 
                  isAI={true}
                />
                <OptionCard 
                  icon={FaFileImport} 
                  title="Embed File into EdDoc" 
                  description="Import and embed external files directly into your EdDoc to create comprehensive learning materials." 
                  onClick={onEmbedFileOpen} 
                />
              </>
          }
        </Grid>
      </Box>
      <ModalBox
        title="Select"
        size="xl"
        description={
          <>
            <FormInput
              label="Title"
              name="title"
              type="text"
              placeholder="eg: Photosynthesis, Human body parts"
              inputOnChange={(e) =>
                setNewFile({ ...newFile, title: e.target.value })
              }
              value={newFile.title}
              formProps={{ isRequired: true }}
              padding="0 0 20px 0"
            />
            <FormControl isRequired={true}>
              <FormLabel
                fontSize="15px"
                textTransform="capitalize"
                fontWeight={600}
              >Select PDF</FormLabel>
              <ResourcesPage type="pdfs" isHideFilter={true} isHideUpload={true} isSelection={true} selectMedia={selectMedia} pdfUrl={mediaUrl} />
            </FormControl>
          </>
        }
        onClose={onCancel}
        isOpen={isCreateFromPDFOpen}
        onCancel={onCancel}
        isFooter={true}
        confirmButtonType={true}
        onConfirm={generatePptDyno}
        confirmButtonText="Create"
        isConfirmButtonDisabled={!mediaUrl || !newFile.title}
      />
      <ModalBox
        title="Select"
        size="xl"
        description={
          <>
            <FormInput
              label="Title"
              name="title"
              type="text"
              placeholder="eg: Photosynthesis, Human body parts"
              inputOnChange={(e) =>
                setNewFile({ ...newFile, title: e.target.value })
              }
              value={newFile.title}
              formProps={{ isRequired: true }}
              padding="0 0 20px 0"
            />
            <Flex padding="0 0 20px 0" alignItems="flex-end">
              <FormSelect
                name="Voice Type"
                label="voiceType"
                formProps={{
                  isRequired: true,
                  maxWidth: "200px",
                }}
                value={newFile.voiceType}
                options={[{ name: "Ava (Female - US)", value: AVA }, { name: "Brandon(Male - US)", value: BRANDON }, { name: "Ada(Female - UK)", value: ADA }, { name: "Ollie(Male - UK)", value: OLLIE }]}
                selectOnChange={(e) => {
                  if (audioRef?.current) {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                  }
                  setNewFile({ ...newFile, voiceType: e.target.value })
                }
                }
              />
              <Button variant="ghost" colorScheme="blue" size="sm" onClick={previewVoice}>Preview Voice</Button>
            </Flex>
            <FormControl isRequired={true}>
              <FormLabel
                fontSize="15px"
                textTransform="capitalize"
                fontWeight={600}
              >Select PPT</FormLabel>
              <ResourcesPage type="ppts" isHideFilter={true} isHideUpload={true} isSelection={true} selectMedia={selectMedia} pptUrl={mediaUrl} />
            </FormControl>
          </>
        }
        onClose={onCancel}
        isOpen={isCreateFromPPTOpen}
        onCancel={onCancel}
        isFooter={true}
        confirmButtonType={true}
        onConfirm={generateVideoDyno}
        confirmButtonText="Create"
        isConfirmButtonDisabled={!mediaUrl || !newFile.title}
      />
      <ModalBox
        title="Select"
        size="xl"
        description={
          <>
            <FormInput
              label="Title"
              name="title"
              type="text"
              placeholder="eg: Photosynthesis, Human body parts"
              inputOnChange={(e) =>
                setNewFile({ ...newFile, title: e.target.value })
              }
              value={newFile.title}
              formProps={{ isRequired: true }}
              padding="0 0 20px 0"
            />
            {selectedDynoType.name === "Reading" && <FormSelect
              formProps={{
                  isRequired: true,
                  maxWidth: "200px",
              }}
              padding="0 0 20px 0"
              label="Type"
              value={contentType}
              options={[
                { name: "📝 Plain Read", value: "STATIC" }, 
                { name: "⚡ Quick Check", value: "QUICKCHECK" }, 
                { name: "🔍 Self Check", value: "SELFCHECK" },
                { name: "🧪 Concept Deep Dive", value: "DEEPDIVE" },
              ]}
              name="contentType"
              selectOnChange={(e) => setContentType(e.target.value)}
            />}
            {selectedDynoType.name === "Reading" && 
              <Flex>
                <InfoIcon mt="3px" />
                <Text fontSize="sm" ml={2} mb={4}>{typeMap[contentType]}</Text>
              </Flex>
            }
            <FormControl isRequired={true}>
              <FormLabel
                fontSize="15px"
                textTransform="capitalize"
                fontWeight={600}
              >Select filetype</FormLabel>
              <ResourcesPage isHideUpload={true} isSelection={true} selectMedia={selectMedia} imageUrl={mediaUrl} videoUrl={mediaUrl} pdfUrl={mediaUrl} audioUrl={mediaUrl} />
            </FormControl>
          </>
        }
        onClose={onCancel}
        isOpen={isOpen}
        onCancel={onCancel}
        isFooter={true}
        confirmButtonType={true}
        isWorking={loading}
        loading={loading}
        loadingText="Processing"
        onConfirm={() => extractText(false)}
        confirmButtonText="Create"
        isConfirmButtonDisabled={!mediaUrl || !newFile.title}
      />
      <ModalBox
        title="Enter Topic/Lesson Name"
        size="xl"
        description={
          <>
            <FormInput
              label="Topic/Lesson Name"
              name="Prompt"
              type="text"
              placeholder=""
              inputOnChange={(e) =>
                setUserMessage(e.target.value)
              }
              value={userMessage}
              formProps={{ isRequired: true }}
              padding="0 0 20px 0"
            />
            {selectedDynoType.name === "Reading" && <FormSelect
              formProps={{
                  isRequired: true,
                  maxWidth: "200px",
              }}
              padding="0 0 20px 0"
              label="Type"
              value={contentType}
              options={[
                { name: "📝 Plain Read", value: "STATIC" }, 
                { name: "⚡ Quick Check", value: "QUICKCHECK" }, 
                { name: "🔍 Self Check", value: "SELFCHECK" },
                { name: "🧪 Concept Deep Dive", value: "DEEPDIVE" },
              ]}
              name="contentType"
              selectOnChange={(e) => setContentType(e.target.value)}
            />}
            {selectedDynoType.name === "Reading" && 
              <Flex>
                <InfoIcon mt="3px" />
                <Text fontSize="sm" ml={2}>{typeMap[contentType]}</Text>
              </Flex>
            }
          </>
        }
        onClose={onCancel}
        isOpen={isPromptOpen}
        onCancel={onCancel}
        isFooter={true}
        confirmButtonType={true}
        isWorking={loading}
        loading={loading}
        loadingText="Processing"
        onConfirm={createDynoUsingPrompt}
        confirmButtonText="Create"
        isConfirmButtonDisabled={!userMessage}
      />
      <ModalBox
        title="Create EdDoc"
        size="xl"
        description={
          <>
            <FormInput
              label="Title"
              name="title"
              type="text"
              placeholder="eg: Photosynthesis, Human body parts"
              inputOnChange={(e) =>
                setNewFile({ ...newFile, title: e.target.value })
              }
              value={newFile.title}
              formProps={{ isRequired: true }}
              padding="0 0 20px 0"
            />
            {selectedDynoType.name === "Reading" && <FormSelect
              formProps={{
                  isRequired: true,
                  maxWidth: "200px",
              }}
              padding="0 0 20px 0"
              label="Type"
              value={contentType}
              options={[
                { name: "📝 Plain Read", value: "STATIC" }, 
                { name: "⚡ Quick Check", value: "QUICKCHECK" }, 
                { name: "🔍 Self Check", value: "SELFCHECK" },
                { name: "🧪 Concept Deep Dive", value: "DEEPDIVE" },
              ]}
              name="contentType"
              selectOnChange={(e) => setContentType(e.target.value)}
            />}
            {selectedDynoType.name === "Reading" && 
              <Flex>
                <InfoIcon mt="3px" />
                <Text fontSize="sm" ml={2} mb={4}>{typeMap[contentType]}</Text>
              </Flex>
            }
            <FormControl colorScheme="brand" isRequired={true}>
              <FormLabel
                fontSize="15px"
                textTransform="capitalize"
                fontWeight={600}
              >Select file</FormLabel>
              <Input
                type="file"
                accept="audio/*,image/*,application/pdf"
                onChange={(e) => {
                  setNewFile(
                    {
                      ...newFile,
                      file: {
                        file: e.target.files[0],
                        name: e.target.files[0].name,
                        size: e.target.files[0].size,
                        fType: e.target.files[0].type.includes("pdf") ? `${e.target.files[0].type.split("/")[1]}s` : `${e.target.files[0].type.split("/")[0]}s`,
                        type: e.target.files[0].type,
                        lastModified: e.target.files[0].lastModified,
                        uuid: uuidv4(),
                        fileType: e.target.files[0].type.includes("pdf") ? e.target.files[0].type.split("/")[1] : e.target.files[0].type.split("/")[0],
                      }
                    })
                }}
              />
            </FormControl>
          </>
        }
        onClose={onCancel}
        isOpen={isBrowseOpen}
        onCancel={onCancel}
        isFooter={true}
        confirmButtonType={true}
        isWorking={isUploading}
        loading={isUploading}
        loadingText={`${uploadProgress.progress ? uploadProgress.progress : 0}% completed`}
        onConfirm={uploadAndCreateEddoc}
        confirmButtonText="Upload and Create"
        isConfirmButtonDisabled={!newFile.title || !newFile.file.name}
      />
      <ModalBox
        title="Embed"
        size="xl"
        description={
          <>
            <FormInput
              label="Title"
              name="title"
              type="text"
              placeholder="eg: Photosynthesis, Human body parts"
              inputOnChange={(e) =>
                setNewFile({ ...newFile, title: e.target.value })
              }
              value={newFile.title}
              formProps={{ isRequired: true }}
              padding="0 0 20px 0"
            />
            <FormControl isRequired={true}>
              {selectedDynoType.name === "Video" ? 
                <FormLabel
                  fontSize="15px"
                  textTransform="capitalize"
                  fontWeight={600}
                >Select file</FormLabel> : 
                <FormLabel
                  fontSize="15px"
                  textTransform="capitalize"
                  fontWeight={600}
                >Select filetype</FormLabel>
              }
              {
                selectedDynoType.name === "Video" ?
                  <ResourcesPage type="videos" isHideFilter={true} isHideUpload={true} isSelection={true} selectMedia={selectMedia} imageUrl={mediaUrl} videoUrl={mediaUrl} pdfUrl={mediaUrl} audioUrl={mediaUrl} /> :
                  <ResourcesPage isHideUpload={true} isSelection={true} selectMedia={selectMedia} imageUrl={mediaUrl} videoUrl={mediaUrl} pdfUrl={mediaUrl} audioUrl={mediaUrl} />
              }
            </FormControl>
          </>
        }
        onClose={onCancel}
        isOpen={isEmbedFileOpen}
        onCancel={onCancel}
        isFooter={true}
        confirmButtonType={true}
        isWorking={loading}
        loading={loading}
        loadingText="Processing"
        onConfirm={embedFileinDyno}
        confirmButtonText="Create"
        isConfirmButtonDisabled={!newFile.title || !mediaUrl}
      />
      <ModalBox
        title="Credits"
        isOpen={isTokenExhaustOpen}
        onClose={onTokenExhaustClose}
        isFooter={true}
        onCancel={onTokenExhaustClose}
        confirmButtonType={true}
        onConfirm={buyCredits}
        isDeleteButton={false}
        confirmButtonText="Buy Credit"
        description={`You’ve used all your available credits.
        Please purchase additional credits to continue using this feature.`}
      />
      <ModalBox
        title="Upgrade"
        isOpen={isTokenExhaustUpgradeToProOpen}
        onClose={onTokenExhaustUpgradeToProClose}
        isFooter={true}
        onCancel={onTokenExhaustUpgradeToProClose}
        confirmButtonType={true}
        onConfirm={upgradeToPro}
        isDeleteButton={false}
        confirmButtonText="Upgrade To Pro"
        description={`You’ve used all your available credits.
        Please upgrade to Pro to continue using this feature.`}
      />
      <ModalBox
        title="Upgrade"
        isOpen={isUpgradeToProOpen}
        onClose={onUpgradeToProClose}
        isFooter={true}
        onCancel={onUpgradeToProClose}
        confirmButtonType={true}
        onConfirm={upgradeToPro}
        isDeleteButton={false}
        confirmButtonText="Upgrade To Pro"
        description={`Please upgrade to Pro to use this feature.`}
      />
    </>
  );
};
